import { createContext, useState, useMemo } from 'react'
import { useProducts } from './ProductContext'

export const CartContext = createContext({
  items: [],
  getProductQuantity: () => {},
  addOneToCart: () => {},
  removeOneFromCart: () => {},
  deleteFromCart: () => {},
  getTotalCost: () => {},
  getCartItemCount: () => {},
  cartTotalCost: 0,
  cartItemCount: 0,
})

export function CartProvider({ children }) {
  const [cartProducts, setCartProducts] = useState([])
  const { getProductData, getCurrentPrice } = useProducts()
  // [ { id: 1 , quantity: 3 }, { id: 2, quantity: 1 } ]

  function getProductQuantity(id) {
    const quantity = cartProducts.find((product) => product.id === id)?.quantity

    if (quantity === undefined) {
      return 0
    }

    return quantity
  }

  function addOneToCart(id) {
    const quantity = getProductQuantity(id)

    if (quantity === 0) {
      // product is not in cart
      setCartProducts([
        ...cartProducts,
        {
          id: id,
          quantity: 1,
        },
      ])
    } else {
      // product is in cart
      setCartProducts(
        cartProducts.map(
          (product) =>
            product.id === id // if condition
              ? { ...product, quantity: product.quantity + 1 } // if statement is true
              : product // if statement is false
        )
      )
    }
  }

  function removeOneFromCart(id) {
    const quantity = getProductQuantity(id)

    if (quantity === 1) {
      deleteFromCart(id)
    } else {
      setCartProducts(
        cartProducts.map(
          (product) =>
            product.id === id // if condition
              ? { ...product, quantity: product.quantity - 1 } // if statement is true
              : product // if statement is false
        )
      )
    }
  }

  function deleteFromCart(id) {
    // [] if an object meets a condition, add the object to array
    // [product1, product2, product3]
    // [product1, product3]
    setCartProducts((cartProducts) =>
      cartProducts.filter((currentProduct) => {
        return currentProduct.id !== id
      })
    )
  }

  // Memoize expensive calculations
  const cartTotalCost = useMemo(() => {
    let totalCost = 0
    cartProducts.forEach((cartItem) => {
      const product = getProductData(cartItem.id)
      if (product) {
        const currentPrice = getCurrentPrice(product)
        if (currentPrice && currentPrice.$numberDecimal) {
          totalCost += parseFloat(currentPrice.$numberDecimal) * cartItem.quantity
        }
      }
    })
    return totalCost
  }, [cartProducts, getProductData, getCurrentPrice])

  const cartItemCount = useMemo(() => {
    return cartProducts.reduce((sum, product) => sum + product.quantity, 0)
  }, [cartProducts])

  function getTotalCost() {
    return cartTotalCost
  }

  function getCartItemCount() {
    return cartItemCount
  }

  const contextValue = {
    items: cartProducts,
    getProductQuantity,
    addOneToCart,
    removeOneFromCart,
    deleteFromCart,
    getTotalCost,
    getCartItemCount,
    cartTotalCost, // Direct access to memoized value
    cartItemCount, // Direct access to memoized value
  }

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
}

export default CartProvider
